import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Flag,
  MessageSquare,
  CheckCircle,
  Search
} from 'lucide-react';
import toast from 'react-hot-toast';

const FlaggedWords = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCard, setEditingCard] = useState(null);
  const [editComment, setEditComment] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadFlaggedCards();
  }, []);

  const loadFlaggedCards = async () => {
    try {
      const response = await api.get('/api/cards/flagged');
      setCards(response.data.cards || []);
    } catch (error) {
      toast.error('Failed to load flagged cards');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFlag = async (cardId) => {
    try {
      await api.post(`/api/cards/${cardId}/flag`, {
        flagged: false,
        user_comment: null
      });
      setCards(cards.filter(c => c.id !== cardId));
      toast.success('Flag removed');
    } catch (error) {
      toast.error('Failed to remove flag');
    }
  };

  const handleUpdateComment = async (card) => {
    try {
      await api.post(`/api/cards/${card.id}/flag`, {
        flagged: true,
        user_comment: editComment
      });
      setCards(cards.map(c => c.id === card.id ? { ...c, user_comment: editComment } : c));
      setEditingCard(null);
      toast.success('Comment updated');
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  const filteredCards = cards.filter(card => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      card.word.includes(search) ||
      card.pinyin?.toLowerCase().includes(search) ||
      card.meaning_en?.toLowerCase().includes(search) ||
      card.meaning_vi?.toLowerCase().includes(search) ||
      card.user_comment?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-amber-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 text-sm">Loading flagged words...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50">
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between max-w-4xl mx-auto w-full">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-amber-600 transition-colors bg-white/60 backdrop-blur-sm px-3 py-2 rounded-xl border border-slate-200 hover:border-amber-200"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Flag className="w-5 h-5 text-amber-500 fill-amber-500" />
          Flagged Words
          <span className="text-sm font-normal text-slate-500">({cards.length})</span>
        </h1>
        <div className="w-20" />
      </div>

      {/* Search */}
      <div className="px-6 max-w-4xl mx-auto w-full mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search flagged words..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none text-sm"
          />
        </div>
      </div>

      {/* Cards List */}
      <div className="px-6 max-w-4xl mx-auto w-full pb-8">
        {filteredCards.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              {searchTerm ? 'No matches found' : 'No Flagged Words'}
            </h2>
            <p className="text-slate-500 mb-6">
              {searchTerm ? 'Try a different search term' : "You haven't flagged any words yet. Flag words during study sessions to review them here."}
            </p>
            {!searchTerm && (
              <button
                onClick={() => navigate('/study')}
                className="px-6 py-3 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors"
              >
                Start Studying
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCards.map((card, index) => (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  {/* Word */}
                  <div className="flex-shrink-0 w-24 text-center">
                    <p className="text-3xl font-bold text-slate-800">{card.word}</p>
                    <p className="text-sm text-slate-400">{card.pinyin}</p>
                    {card.hsk_level && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded-full text-xs font-semibold">
                        HSK {card.hsk_level}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="space-y-1.5 mb-3">
                      {card.meaning_en && (
                        <p className="text-sm text-blue-600">
                          <span className="font-semibold text-blue-500">EN: </span>{card.meaning_en}
                        </p>
                      )}
                      {card.meaning_vi && (
                        <p className="text-sm text-emerald-600">
                          <span className="font-semibold text-emerald-500">VI: </span>{card.meaning_vi}
                        </p>
                      )}
                    </div>

                    {/* Comment */}
                    {editingCard === card.id ? (
                      <div className="mt-3">
                        <textarea
                          value={editComment}
                          onChange={(e) => setEditComment(e.target.value)}
                          placeholder="Add a comment about this word..."
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none resize-none"
                          rows={2}
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleUpdateComment(card)}
                            className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingCard(null)}
                            className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : card.user_comment ? (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <MessageSquare className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-amber-800 flex-1">{card.user_comment}</p>
                          <button
                            onClick={() => { setEditingCard(card.id); setEditComment(card.user_comment); }}
                            className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingCard(card.id); setEditComment(''); }}
                        className="mt-3 text-xs text-slate-400 hover:text-amber-500 font-medium flex items-center gap-1"
                      >
                        <MessageSquare className="w-3 h-3" />
                        Add comment
                      </button>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => handleRemoveFlag(card.id)}
                      className="p-2 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                      title="Remove flag"
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlaggedWords;
